import { useEffect, useRef, useState } from "react";

const steps = [
  {
    num: "01",
    title: "Discovery & Scoping",
    desc: "We start by deeply understanding your business goals, technical requirements, and user needs through focused discovery sessions and stakeholder interviews.",
    icon: "ri-search-eye-line",
    duration: "1–2 weeks",
  },
  {
    num: "02",
    title: "Architecture & Planning",
    desc: "We design the system architecture, define the tech stack, and create a detailed project roadmap with clear milestones and deliverables.",
    icon: "ri-git-branch-line",
    duration: "1 week",
  },
  {
    num: "03",
    title: "UI/UX Design",
    desc: "We craft high-fidelity wireframes and interactive prototypes. We iterate quickly based on your feedback until every detail feels exactly right.",
    icon: "ri-layout-masonry-line",
    duration: "2–3 weeks",
  },
  {
    num: "04",
    title: "Agile Development",
    desc: "We build in two-week sprints with regular demos and check-ins. Clean, well-documented code with full test coverage — built to last.",
    icon: "ri-terminal-box-line",
    duration: "4–12 weeks",
  },
  {
    num: "05",
    title: "QA & Testing",
    desc: "Rigorous automated and manual testing across all devices and environments. We don't ship until everything is bulletproof.",
    icon: "ri-shield-check-line",
    duration: "1–2 weeks",
  },
  {
    num: "06",
    title: "Launch & Support",
    desc: "We handle deployment, monitoring, and post-launch optimization. Ongoing maintenance and feature development available as your product grows.",
    icon: "ri-rocket-2-line",
    duration: "Ongoing",
  },
];

export default function Process() {
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-36 bg-gray-950">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`mb-16 md:mb-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4 block">How We Work</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Our Development<br />
              <span className="italic font-light text-gray-500">Process</span>
            </h2>
            <p className="text-gray-400 max-w-sm text-base leading-relaxed">
              A proven six-step engineering framework we follow to deliver exceptional software — on time, every time.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {steps.map((step, i) => (
            <div
              key={step.num}
              onMouseEnter={() => setActiveStep(i)}
              onMouseLeave={() => setActiveStep(null)}
              className="group relative p-7 rounded-2xl border border-white/10 hover:border-white/30 bg-white/[0.03] hover:bg-white/[0.07] transition-all duration-300 cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms`,
              }}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="text-4xl font-bold text-white/10 group-hover:text-white/20 transition-colors">{step.num}</span>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 group-hover:bg-white/10 transition-colors">
                  <i className={`${step.icon} text-lg text-white/50 group-hover:text-white transition-colors`}></i>
                </div>
              </div>

              <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">{step.desc}</p>

              <div className="flex items-center gap-2">
                <i className="ri-time-line text-gray-600 text-sm"></i>
                <span className="text-gray-500 text-xs">{step.duration}</span>
              </div>

              <div className={`absolute bottom-0 left-7 right-7 h-px bg-white/30 transition-all duration-300 ${activeStep === i ? "opacity-100" : "opacity-0"}`}></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${visible ? "opacity-100" : "opacity-0"}`}>
          <p className="text-gray-500 mb-6 text-sm">Ready to bring your idea to life?</p>
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-3 bg-white text-black font-semibold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-rocket-line"></i>
            Start a Project With Us
          </button>
        </div>
      </div>
    </section>
  );
}
